// 主题色工具方法
// TTheme: { r, g, b, a, hex }

/**
 * 数字转为两位十六进制字符串
 * @param {number} n
 * @returns {string}
 */
function toHex(n) {
  return n.toString(16).padStart(2, '0')
}

/**
 * 根据明暗和比例调整颜色，生成新的颜色对象
 * @param {Object} color 颜色对象 {r,g,b,a,hex}
 * @param {number} ti 调整比例 0-1
 * @param {boolean} isDark 是否为暗色模式
 * @returns {Object} 新的颜色对象
 */
export const tintColor = (color, ti, isDark) => {
  if (ti == 0) {
    let hex = color.hex
    if (!hex || typeof hex !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(hex)) {
      hex = `#${toHex(color.r)}${toHex(color.g)}${toHex(color.b)}`
    }
    return {
      r: color.r,
      g: color.g,
      b: color.b,
      a: color.a,
      hex
    }
  }
  let red = parseInt(color.r + '')
  let green = parseInt(color.g + '')
  let blue = parseInt(color.b + '')
  if (isDark) {
    red += Math.round(ti * (255 / 2 - 50 - red))
    green += Math.round(ti * (255 / 2 - 50 - green))
    blue += Math.round(ti * (255 / 2 - 50 - blue))
  } else {
    red += Math.round(ti * (255 - red))
    green += Math.round(ti * (255 - green))
    blue += Math.round(ti * (255 - blue))
  }
  red = Math.max(0, Math.min(255, red))
  green = Math.max(0, Math.min(255, green))
  blue = Math.max(0, Math.min(255, blue))
  const hex = `#${toHex(red)}${toHex(green)}${toHex(blue)}`
  return {
    r: red,
    g: green,
    b: blue,
    a: color.a,
    hex
  }
}

/**
 * 获取主色的不同深浅色阶数组，并设置到 CSS 变量
 * @param {Object} rgba 颜色对象
 * @param {boolean} isDark 是否暗色
 * @returns {Array} 色阶数组
 */
export const getPrimaryColors = (rgba, isDark) => {
  const clusters = []
  for (let i = 0; i <= 9; i++) {
    const color = tintColor(rgba, Number((i / 10).toFixed(2)), isDark)
    clusters.push(color)
    if (typeof document !== 'undefined') {
      if (!color.hex || typeof color.hex !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(color.hex)) continue;
      if (i == 0) {
        document.documentElement.style.setProperty('--el-color-primary', color.hex)
      } else if (i == 1) {
        document.documentElement.style.setProperty('--el-color-primary-dark-2', color.hex)
      } else {
        document.documentElement.style.setProperty('--el-color-primary-light-' + i, color.hex)
      }
    }
  }
  return clusters
}


/**
 * 判断是否 十六进制颜色值.
 * 输入形式可为 #fff000 #f00
 *
 * @param   String  color   十六进制颜色值
 * @return  Boolean
 */
export const isHexColor = (color) => {
  const reg = /^#([0-9a-fA-F]{3}|[0-9a-fA-f]{6})$/
  return reg.test(color)
}

/**
 * RGB 颜色值转换为 十六进制颜色值.
 * r, g, 和 b 需要在 [0, 255] 范围内
 *
 * @return  String          类似#ff00ff
 * @param r
 * @param g
 * @param b
 */
export const rgbToHex = (r, g, b) => {
  // tslint:disable-next-line:no-bitwise
  const hex = ((r << 16) | (g << 8) | b).toString(16)
  return '#' + new Array(Math.abs(hex.length - 7)).join('0') + hex
}

/**
 * 将 HEX 颜色转换为其 RGB 表示
 * @param {string} hex 要转换的颜色
 * @returns 传入颜色的 RGB 表示
 */
export const hexToRGB = (hex, opacity) => {
  let sHex = hex.toLowerCase()
  if (isHexColor(hex)) {
    if (sHex.length === 4) {
      let sColorNew = '#'
      for (let i = 1; i < 4; i += 1) {
        sColorNew += sHex.slice(i, i + 1).concat(sHex.slice(i, i + 1))
      }
      sHex = sColorNew
    }
    const sColorChange = []
    for (let i = 1; i < 7; i += 2) {
      sColorChange.push(parseInt('0x' + sHex.slice(i, i + 2)))
    }
    return opacity
      ? 'RGBA(' + sColorChange.join(',') + ',' + opacity + ')'
      : 'RGB(' + sColorChange.join(',') + ')'
  }
  return sHex
}

/**
 * 判断颜色是否为深色
 * @param {string} color HEX 颜色
 * @returns {boolean}
 */
export const colorIsDark = (color) => {
  if (!isHexColor(color)) return
  const [r, g, b] = hexToRGB(color)
    .replace(/(?:\(|\)|rgb|RGB)*/g, '')
    .split(',')
    .map((item) => Number(item))
  return r * 0.299 + g * 0.578 + b * 0.114 < 192
}

/**
 * 使 HEX 颜色变暗，按传入的百分比
 * @param {string} color 要处理的颜色
 * @param {number} amount 颜色变化的百分比
 * @returns {string} 处理后的 HEX 颜色
 */
export const darken = (color, amount) => {
  color = color.indexOf('#') >= 0 ? color.substring(1, color.length) : color
  amount = Math.trunc((255 * amount) / 100)
  return `#${subtractLight(color.substring(0, 2), amount)}${subtractLight(
    color.substring(2, 4),
    amount
  )}${subtractLight(color.substring(4, 6), amount)}`
}

/**
 * 使 6 位 HEX 颜色变亮，按传入的百分比
 * @param {string} color 要处理的颜色
 * @param {number} amount 颜色变化的百分比
 * @returns {string} 处理后的 HEX 颜色
 */
export const lighten = (color, amount) => {
  color = color.indexOf('#') >= 0 ? color.substring(1, color.length) : color
  amount = Math.trunc((255 * amount) / 100)
  return `#${addLight(color.substring(0, 2), amount)}${addLight(
    color.substring(2, 4),
    amount
  )}${addLight(color.substring(4, 6), amount)}`
}

/* Suma el porcentaje indicado a un color (RR, GG o BB) hexadecimal para aclararlo */
/**
 * 为 HEX 颜色的 R、G 或 B 分量增加指定百分比，使其变亮
 * @param {string} color 要处理的颜色分量
 * @param {number} amount 变化的百分比
 * @returns {string} 处理后的颜色分量
 */
const addLight = (color, amount) => {
  const cc = parseInt(color, 16) + amount
  const c = cc > 255 ? 255 : cc
  return c.toString(16).length > 1 ? c.toString(16) : `0${c.toString(16)}`
}

/**
 * 计算 rgb 颜色的亮度
 * @param {number} r 红色分量
 * @param {number} g 绿色分量
 * @param {number} b 蓝色分量
 * @returns {number} 亮度值
 */
const luminanace = (r, g, b) => {
  const a = [r, g, b].map((v) => {
    v /= 255
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
  })
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722
}

/**
 * 计算两个 rgb 颜色之间的对比度
 * @param {string} rgb1 rgb 颜色1
 * @param {string} rgb2 rgb 颜色2
 * @returns {number} 对比度
 */
const contrast = (rgb1, rgb2) => {
  return (
    (luminanace(~~rgb1[0], ~~rgb1[1], ~~rgb1[2]) + 0.05) /
    (luminanace(rgb2[0], rgb2[1], rgb2[2]) + 0.05)
  )
}

/**
 * 根据与背景的对比度，判断最佳文本颜色（黑色或白色）
 * @param hexColor - 用户最后选择的颜色
 * @returns {string} #000000 或 #FFFFFF
 */
export const calculateBestTextColor = (hexColor) => {
  const rgbColor = hexToRGB(hexColor.substring(1))
  const contrastWithBlack = contrast(rgbColor.split(','), [0, 0, 0])

  return contrastWithBlack >= 12 ? '#000000' : '#FFFFFF'
}

/**
 * 为 HEX 颜色的 R、G 或 B 分量减少指定百分比，使其变暗
 * @param {string} color 要处理的颜色分量
 * @param {number} amount 变化的百分比
 * @returns {string} 处理后的颜色分量
 */
const subtractLight = (color, amount) => {
  const cc = parseInt(color, 16) - amount
  const c = cc < 0 ? 0 : cc
  return c.toString(16).length > 1 ? c.toString(16) : `0${c.toString(16)}`
}

/**
 * 混合两种颜色。
 *
 * @param {string} color1 - 第一个颜色，必须是以 `#` 开头的 6 位十六进制颜色码。
 * @param {string} color2 - 第二个颜色，必须是以 `#` 开头的 6 位十六进制颜色码。
 * @param {number} [weight=0.5] - color1 在混合中的权重，取值范围为 0 到 1，0 表示 100% color2，1 表示 100% color1。
 * @returns {string} 混合后的颜色，以 `#` 开头的 6 位十六进制颜色码。
 */
export const mix = (color1, color2, weight = 0.5) => {
  let color = '#'
  for (let i = 0; i <= 2; i++) {
    const c1 = parseInt(color1.substring(1 + i * 2, 3 + i * 2), 16)
    const c2 = parseInt(color2.substring(1 + i * 2, 3 + i * 2), 16)
    const c = Math.round(c1 * weight + c2 * (1 - weight))
    color += c.toString(16).padStart(2, '0')
  }
  return color
}
